import { MarkerClusterer } from "@googlemaps/markerclusterer";
import {
  type HailReport,
  type MapAppointment,
  type MapConfig,
  type MapFilters,
  type MapJob,
  type MapView,
  type WorkflowItem,
  METERS_PER_MILE,
  distanceMeters,
  filterMapJobs,
  hailColor,
  hailName,
  mapFiltersSchema,
} from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { errorMessage, get, patch, put } from "../api";
import { AddressActions } from "../components/address-actions";
import { toDateInput } from "../components/schedule";
import { Alert, Button, Checkbox, Loading, PageHeader, SelectField, StageBadge, cx, formatCents } from "../components/ui";
import { loadGoogleMaps, onGoogleMapsAuthFailure } from "../lib/google-maps";
import { type HeatOverlay, createHeatOverlay } from "../lib/heat-overlay";
import { useUsers, useWorkflows } from "../lookups";
import { useSession } from "../session";

const STORE = "nmt.map.filters";
const DEFAULTS = mapFiltersSchema.parse({});
/** Distinct colors for job types and for stages within a type (closed and lost have their own). */
const PALETTE = ["#2563EB", "#EA580C", "#9333EA", "#0891B2", "#DB2777", "#CA8A04", "#4F46E5", "#0D9488", "#C2410C", "#7C3AED", "#65A30D", "#BE123C"];
const CLOSED = "#16A34A";
const LOST = "#9CA3AF";

function loadFilters(): MapFilters {
  try {
    const raw = localStorage.getItem(STORE);
    if (raw) return mapFiltersSchema.parse(JSON.parse(raw));
  } catch {
    // Old or unreadable: start fresh.
  }
  return DEFAULTS;
}

type Legend = { label: string; color: string; count: number }[];

/** How a job's pin is colored, and the legend for it. */
function colorer(colorBy: MapFilters["colorBy"], workflows: WorkflowItem[]) {
  const stageColor = new Map<string, string>();
  const typeColor = new Map<string, string>();
  workflows.forEach((w, wi) => {
    typeColor.set(w.id, PALETTE[wi % PALETTE.length]!);
    w.stages.filter((s) => s.kind === "open").forEach((s, si) => stageColor.set(s.id, PALETTE[si % PALETTE.length]!));
  });
  return (j: MapJob): { color: string; label: string } => {
    if (colorBy === "type") return { color: typeColor.get(j.workflowId) ?? "#64748B", label: j.workflowName };
    if (colorBy === "rep") return { color: j.salesRep?.color ?? "#64748B", label: j.salesRep?.name ?? "No rep" };
    if (j.stageKind === "closed") return { color: CLOSED, label: "Closed (any type)" };
    if (j.stageKind === "lost") return { color: LOST, label: "Lost (any type)" };
    return { color: stageColor.get(j.stageId) ?? "#64748B", label: j.stageName };
  };
}

const iso = (d: Date) => toDateInput(d);
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return iso(d);
};

export function MapPage() {
  const { can } = useSession();
  const qc = useQueryClient();
  const config = useQuery({ queryKey: ["map-config"], queryFn: () => get<MapConfig>("/api/map/config") });
  const jobs = useQuery({ queryKey: ["map-jobs"], queryFn: () => get<MapJob[]>("/api/map/jobs"), staleTime: 60_000 });
  const workflows = useWorkflows();
  const users = useUsers();
  const [filters, setFilters] = useState<MapFilters>(loadFilters);
  const set = (patchF: Partial<MapFilters>) => setFilters((f) => ({ ...f, ...patchF }));
  useEffect(() => {
    try {
      localStorage.setItem(STORE, JSON.stringify(filters));
    } catch {
      // Private window: filters just aren't remembered.
    }
  }, [filters]);
  const [viewId, setViewId] = useState("");
  const [mode, setMode] = useState<"map" | "list">("map");
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [hailPick, setHailPick] = useState<HailReport | null>(null);
  const [placing, setPlacing] = useState<MapJob | null>(null);
  const [pickingSpot, setPickingSpot] = useState(false);
  const [spot, setSpot] = useState<{ lat: number; lon: number } | null>(null);
  const [spotMiles, setSpotMiles] = useState(1);
  const [day, setDay] = useState({ on: false, date: iso(new Date()), userId: "" });
  const [fitTick, setFitTick] = useState(0);
  const [mapError, setMapError] = useState<string | null>(null);

  const wfs = useMemo(() => (workflows.data ?? []).filter((w) => !w.archived), [workflows.data]);
  const all = useMemo(() => jobs.data ?? [], [jobs.data]);

  // Hail: NOAA's reports in the area around the jobs (or around the office).
  const box = useMemo(() => {
    const placed = all.filter((j) => j.lat !== null && j.lon !== null);
    const home = config.data?.home;
    if (!placed.length && !home) return null;
    const lats = placed.length ? placed.map((j) => j.lat!) : [home!.lat];
    const lons = placed.length ? placed.map((j) => j.lon!) : [home!.lon];
    const pad = 1;
    return { south: Math.min(...lats) - pad, north: Math.max(...lats) + pad, west: Math.min(...lons) - pad, east: Math.max(...lons) + pad };
  }, [all, config.data?.home]);
  const hailQs = new URLSearchParams({ from: daysAgo(filters.hail.days), to: iso(new Date()), minSize: String(filters.hail.minSize), ...(box ? Object.fromEntries(Object.entries(box).map(([k, v]) => [k, v.toFixed(3)])) : {}) });
  const hail = useQuery({
    queryKey: ["map-hail", hailQs.toString()],
    queryFn: () => get<{ reports: HailReport[]; complete: boolean }>(`/api/map/hail?${hailQs}`),
    enabled: filters.hail.on,
    staleTime: 10 * 60_000,
  });
  const reports = useMemo(() => (filters.hail.on ? (hail.data?.reports ?? []) : []), [filters.hail.on, hail.data]);

  const dayFrom = new Date(`${day.date}T00:00:00`);
  const dayQs = new URLSearchParams({ from: dayFrom.toISOString(), to: new Date(dayFrom.getTime() + 86_400_000).toISOString(), ...(day.userId ? { userId: day.userId } : {}) });
  const stops = useQuery({ queryKey: ["map-day", dayQs.toString()], queryFn: () => get<MapAppointment[]>(`/api/map/appointments?${dayQs}`), enabled: day.on });

  const shown = useMemo(() => filterMapJobs(all, filters, reports), [all, filters, reports]);
  const onMap = useMemo(() => shown.filter((j) => j.lat !== null && j.lon !== null), [shown]);
  const offMap = useMemo(() => shown.filter((j) => j.lat === null || j.lon === null), [shown]);
  const colorOf = useMemo(() => colorer(filters.colorBy, wfs), [filters.colorBy, wfs]);
  const legend: Legend = useMemo(() => {
    const m = new Map<string, Legend[number]>();
    for (const j of onMap) {
      const c = colorOf(j);
      const e = m.get(c.label) ?? { label: c.label, color: c.color, count: 0 };
      e.count++;
      m.set(c.label, e);
    }
    return [...m.values()].sort((a, b) => b.count - a.count);
  }, [onMap, colorOf]);
  const nearSpot = useMemo(() => {
    if (!spot) return [];
    return onMap
      .map((j) => ({ j, m: distanceMeters(spot, { lat: j.lat!, lon: j.lon! }) }))
      .filter((x) => x.m <= spotMiles * METERS_PER_MILE)
      .sort((a, b) => a.m - b.m);
  }, [onMap, spot, spotMiles]);

  const pin = useMutation({
    mutationFn: (v: { job: MapJob; at: { lat: number; lon: number } | null }) => put(`/api/map/jobs/${v.job.id}/pin`, v.at ?? { clear: true }),
    onSuccess: () => {
      setPlacing(null);
      qc.invalidateQueries({ queryKey: ["map-jobs"] });
    },
  });
  const views = config.data?.views ?? [];
  const saveViews = useMutation({
    mutationFn: (next: MapView[]) => patch("/api/settings", { "map.views": next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["map-config"] }),
  });

  const key = config.data?.googleMaps;
  const effectiveMode = key && !mapError ? mode : "list";
  const selectedJob = selected ? all.find((j) => j.id === selected) ?? null : null;
  const hasMoney = can("money.view");

  const onMapClick = (at: { lat: number; lon: number }) => {
    if (placing) pin.mutate({ job: placing, at });
    else if (pickingSpot) {
      setSpot(at);
      setPickingSpot(false);
    } else {
      setSelected(null);
      setHailPick(null);
    }
  };

  const filtersPanel = (
    <div className="flex flex-col gap-4 text-sm">
      <ViewPicker
        views={views}
        viewId={viewId}
        canSave={!!config.data?.canSaveViews}
        busy={saveViews.isPending}
        onPick={(v) => {
          setViewId(v?.id ?? "");
          if (v) setFilters(mapFiltersSchema.parse(v.filters));
        }}
        onSave={(name) => {
          const id = viewId && views.some((v) => v.id === viewId && v.name === name) ? viewId : crypto.randomUUID().slice(0, 12);
          saveViews.mutate([...views.filter((v) => v.id !== id), { id, name, filters }]);
          setViewId(id);
        }}
        onDelete={() => {
          saveViews.mutate(views.filter((v) => v.id !== viewId));
          setViewId("");
        }}
      />
      {saveViews.error && <Alert>{errorMessage(saveViews.error)}</Alert>}

      <Section title="Jobs">
        <Chips
          options={[
            { id: "open", label: "Open" },
            { id: "closed", label: "Closed" },
            { id: "lost", label: "Lost" },
          ]}
          value={filters.status}
          onChange={(v) => set({ status: v as MapFilters["status"] })}
        />
      </Section>
      <Section title="Job type">
        <Chips options={wfs.map((w) => ({ id: w.id, label: w.name }))} value={filters.workflowIds} onChange={(v) => set({ workflowIds: v, stageIds: [] })} />
        {filters.workflowIds.length === 1 && (
          <div className="mt-2">
            <div className="mb-1 text-xs font-semibold text-muted">Stage</div>
            <Chips options={(wfs.find((w) => w.id === filters.workflowIds[0])?.stages ?? []).map((s) => ({ id: s.id, label: s.name }))} value={filters.stageIds} onChange={(v) => set({ stageIds: v })} />
          </div>
        )}
      </Section>
      <Section title="Rep">
        <Chips options={[...(users.data ?? []).filter((u) => u.active).map((u) => ({ id: u.id, label: u.name.split(" ")[0]! })), { id: "none", label: "No rep" }]} value={filters.repIds} onChange={(v) => set({ repIds: v })} />
      </Section>
      <Section title="Dates">
        <select className={inputClass} value={filters.dateField} onChange={(e) => set({ dateField: e.target.value as MapFilters["dateField"] })} aria-label="Which date">
          <option value="created">Created</option>
          <option value="sold">Sold (signed or first invoiced)</option>
          <option value="changed">Moved to its stage</option>
        </select>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <input type="date" className={inputClass} value={filters.from} onChange={(e) => set({ from: e.target.value })} aria-label="From" />
          <input type="date" className={inputClass} value={filters.to} onChange={(e) => set({ to: e.target.value })} aria-label="To" />
        </div>
        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs">
          <button className="text-brand hover:underline" onClick={() => set({ from: `${new Date().getFullYear()}-01-01`, to: "" })}>
            This year
          </button>
          <button className="text-brand hover:underline" onClick={() => set({ from: `${new Date().getFullYear() - 1}-01-01`, to: `${new Date().getFullYear() - 1}-12-31` })}>
            Last year
          </button>
          <button className="text-brand hover:underline" onClick={() => set({ from: daysAgo(365), to: "" })}>
            Last 12 months
          </button>
          {(filters.from || filters.to) && (
            <button className="text-muted hover:underline" onClick={() => set({ from: "", to: "" })}>
              Any time
            </button>
          )}
        </div>
      </Section>
      <Section title="More">
        <input className={inputClass} placeholder="Search name, address, job #" value={filters.search} onChange={(e) => set({ search: e.target.value })} />
        <input className={cx(inputClass, "mt-2")} placeholder="Insurance carrier" value={filters.carrier} onChange={(e) => set({ carrier: e.target.value })} />
        {hasMoney && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <input type="number" min={0} className={inputClass} placeholder="Min $" value={filters.minValue ?? ""} onChange={(e) => set({ minValue: e.target.value ? Math.max(0, Math.round(Number(e.target.value))) : null })} />
            <input type="number" min={0} className={inputClass} placeholder="Max $" value={filters.maxValue ?? ""} onChange={(e) => set({ maxValue: e.target.value ? Math.max(0, Math.round(Number(e.target.value))) : null })} />
          </div>
        )}
      </Section>
      <Section title="Color pins by">
        <select className={inputClass} value={filters.colorBy} onChange={(e) => set({ colorBy: e.target.value as MapFilters["colorBy"] })} aria-label="Color pins by">
          <option value="stage">Stage</option>
          <option value="type">Job type</option>
          <option value="rep">Commissioned rep</option>
        </select>
      </Section>

      <Section title="Overlays">
        <Checkbox label="Heat map" description="Where the jobs shown are concentrated." checked={filters.heat} onChange={(v) => set({ heat: v })} />
        <Checkbox label="Hail reports" description="NOAA storm reports (spotters, the public, officials)." checked={filters.hail.on} onChange={(v) => set({ hail: { ...filters.hail, on: v } })} />
        {filters.hail.on && (
          <div className="ml-8 flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2">
              <select className={inputClass} value={filters.hail.days} onChange={(e) => set({ hail: { ...filters.hail, days: Number(e.target.value) } })} aria-label="Hail from">
                {[3, 7, 14, 30, 60, 90, 180, 365, 730].map((d) => (
                  <option key={d} value={d}>
                    {d < 365 ? `Last ${d} days` : d === 365 ? "Last year" : "Last 2 years"}
                  </option>
                ))}
              </select>
              <select className={inputClass} value={filters.hail.minSize} onChange={(e) => set({ hail: { ...filters.hail, minSize: Number(e.target.value) } })} aria-label="Smallest hail">
                {[0.75, 1, 1.25, 1.5, 1.75, 2, 2.5].map((s) => (
                  <option key={s} value={s}>
                    {s}"+ ({hailName(s).toLowerCase()})
                  </option>
                ))}
              </select>
            </div>
            <Checkbox label="Only jobs near hail" checked={filters.hail.nearOnly} onChange={(v) => set({ hail: { ...filters.hail, nearOnly: v } })} />
            <select className={inputClass} value={filters.hail.miles} onChange={(e) => set({ hail: { ...filters.hail, miles: Number(e.target.value) } })} aria-label="How near">
              {[0.25, 0.5, 1, 2, 3, 5].map((m) => (
                <option key={m} value={m}>
                  Within {m} mi of a report
                </option>
              ))}
            </select>
            {hail.isLoading && <span className="text-xs text-muted">Reading NOAA's reports… (a long range takes a bit the first time)</span>}
            {hail.data && (
              <span className="text-xs text-muted">
                {hail.data.reports.length} reports{hail.data.complete ? "" : " so far (some days didn't load; they'll be tried again)"}. Hail reports are points where someone saw hail, not the storm's full path.
              </span>
            )}
            {hail.error && <Alert>{errorMessage(hail.error)}</Alert>}
          </div>
        )}
        <Checkbox label="A day's appointments" description="In order, with a route." checked={day.on} onChange={(v) => setDay({ ...day, on: v })} />
        {day.on && (
          <div className="ml-8 grid grid-cols-2 gap-2">
            <input type="date" className={inputClass} value={day.date} onChange={(e) => setDay({ ...day, date: e.target.value || iso(new Date()) })} aria-label="Day" />
            <select className={inputClass} value={day.userId} onChange={(e) => setDay({ ...day, userId: e.target.value })} aria-label="Whose">
              <option value="">Mine</option>
              {(users.data ?? [])
                .filter((u) => u.active)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
            </select>
          </div>
        )}
      </Section>

      <Section title="Jobs near a spot">
        <div className="flex flex-wrap gap-2">
          <Button variant={pickingSpot ? "primary" : "secondary"} onClick={() => (setPickingSpot(!pickingSpot), setPlacing(null))} disabled={effectiveMode !== "map"}>
            {pickingSpot ? "Click the map…" : "Pick a spot"}
          </Button>
          <Button
            onClick={() =>
              navigator.geolocation?.getCurrentPosition(
                (p) => setSpot({ lat: p.coords.latitude, lon: p.coords.longitude }),
                () => setMapError("Couldn't get your location. Allow location for this site and try again."),
                { enableHighAccuracy: true, timeout: 10_000 },
              )
            }
          >
            Where I am
          </Button>
        </div>
        {spot && (
          <div className="mt-2 flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <select className={inputClass} value={spotMiles} onChange={(e) => setSpotMiles(Number(e.target.value))} aria-label="Within">
                {[0.25, 0.5, 1, 2, 5, 10].map((m) => (
                  <option key={m} value={m}>
                    Within {m} mi
                  </option>
                ))}
              </select>
              <button className="text-xs text-muted hover:underline" onClick={() => setSpot(null)}>
                Clear
              </button>
            </div>
            <div className="text-xs font-semibold text-ink-soft">{nearSpot.length} jobs shown are within {spotMiles} mi</div>
            <ul className="max-h-60 overflow-y-auto">
              {nearSpot.map(({ j, m }) => (
                <li key={j.id}>
                  <button className="w-full truncate rounded px-1 py-1 text-left hover:bg-mist" onClick={() => setSelected(j.id)}>
                    <span className="font-semibold">#{j.number}</span> {j.customerName} <span className="text-muted">· {(m / METERS_PER_MILE).toFixed(1)} mi</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      {offMap.length > 0 && (
        <Section title={`Not on the map (${offMap.length})`}>
          <ul className="max-h-72 overflow-y-auto">
            {offMap.map((j) => (
              <li key={j.id} className="flex items-center gap-2 border-b border-mist py-1.5 last:border-0">
                <span className="min-w-0 flex-1">
                  <Link to={`/jobs/${j.id}`} className="block truncate font-semibold hover:underline">
                    #{j.number} {j.customerName}
                  </Link>
                  <span className="block truncate text-xs text-muted">
                    {j.placed === "none" ? "No address" : j.placed === "pending" ? "Not looked up yet (within the hour)" : "Address not found"}
                    {j.address ? ` · ${j.address}` : ""}
                  </span>
                </span>
                {config.data?.canPlace && effectiveMode === "map" && (
                  <button className="shrink-0 text-xs font-semibold text-brand hover:underline" onClick={() => (setPlacing(j), setPickingSpot(false), setShowFilters(false))}>
                    Place it
                  </button>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Map"
        description={jobs.data ? `${onMap.length} jobs on the map${offMap.length ? `, ${offMap.length} not placed` : ""}${shown.length !== all.length ? ` (of ${all.length})` : ""}.` : undefined}
        action={
          <div className="flex flex-wrap gap-2">
            <Button className="lg:hidden" onClick={() => setShowFilters(!showFilters)}>
              {showFilters ? "Hide filters" : "Filters"}
            </Button>
            {key && !mapError && (
              <div className="flex rounded-[var(--radius-control)] bg-mist p-0.5">
                {(["map", "list"] as const).map((m) => (
                  <button key={m} className={cx("rounded px-3 py-1.5 text-sm font-medium", mode === m ? "bg-white shadow-sm" : "text-muted")} onClick={() => setMode(m)}>
                    {m === "map" ? "Map" : "List"}
                  </button>
                ))}
              </div>
            )}
            {effectiveMode === "map" && <Button onClick={() => setFitTick((t) => t + 1)}>Fit to jobs</Button>}
          </div>
        }
      />
      {config.data && !key && <Alert tone="info">The map isn't set up on this server yet (it needs the Google Maps key). Here's the list in the meantime.</Alert>}
      {mapError && <div className="mb-3"><Alert>{mapError}</Alert></div>}
      {(jobs.error || config.error) && <Alert>{errorMessage(jobs.error ?? config.error)}</Alert>}
      {placing && (
        <div className="mb-3">
          <Alert tone="info">
            Click the map where #{placing.number} {placing.customerName} is.{" "}
            <button className="font-semibold underline" onClick={() => setPlacing(null)}>
              Cancel
            </button>
            {pin.error && <span className="ml-2 text-danger">{errorMessage(pin.error)}</span>}
          </Alert>
        </div>
      )}

      <div className="mt-3 flex flex-col gap-4 lg:flex-row">
        <aside className={cx("rounded-[var(--radius-panel)] bg-white p-4 shadow-[var(--shadow-card)] lg:block lg:max-h-[calc(100vh-10rem)] lg:w-80 lg:shrink-0 lg:overflow-y-auto", showFilters ? "block" : "hidden")}>{filtersPanel}</aside>
        <div className="min-w-0 flex-1">
          {jobs.isLoading || config.isLoading ? (
            <Loading />
          ) : effectiveMode === "map" ? (
            <div className="relative h-[65vh] overflow-hidden rounded-[var(--radius-panel)] shadow-[var(--shadow-card)] lg:h-[calc(100vh-10rem)]">
              <JobsMap
                apiKey={key!.apiKey}
                mapId={key!.mapId}
                jobs={onMap}
                colorOf={colorOf}
                heat={filters.heat}
                hail={reports}
                hailMiles={filters.hail.miles}
                stops={day.on ? (stops.data ?? []) : []}
                spot={spot}
                spotMiles={spotMiles}
                home={config.data?.home ?? null}
                crosshair={!!placing || pickingSpot}
                fitTick={fitTick}
                selectedId={selected}
                onJob={(id) => (setSelected(id), setHailPick(null))}
                onHail={(h) => (setHailPick(h), setSelected(null))}
                onClick={onMapClick}
                onError={setMapError}
              />
              {legend.length > 0 && <LegendBox legend={legend} />}
              {selectedJob && (
                <JobCard
                  job={selectedJob}
                  canPlace={!!config.data?.canPlace}
                  onClose={() => setSelected(null)}
                  onMove={() => (setPlacing(selectedJob), setSelected(null))}
                  onBackToAddress={() => pin.mutate({ job: selectedJob, at: null })}
                />
              )}
              {hailPick && <HailCard report={hailPick} miles={filters.hail.miles} jobsNear={onMap.filter((j) => distanceMeters(hailPick, { lat: j.lat!, lon: j.lon! }) <= filters.hail.miles * METERS_PER_MILE).length} onClose={() => setHailPick(null)} />}
            </div>
          ) : (
            <JobList jobs={shown} hasMoney={hasMoney} />
          )}
          {day.on && <DayList stops={stops.data ?? []} loading={stops.isLoading} />}
        </div>
      </div>
    </div>
  );
}

const inputClass = "w-full rounded-[var(--radius-control)] border border-line bg-white px-2.5 py-1.5 text-base sm:text-sm";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-1.5 text-xs font-semibold tracking-wide text-ink-soft uppercase">{title}</h3>
      {children}
    </section>
  );
}

/** Toggle chips; none picked means all. */
function Chips({ options, value, onChange }: { options: { id: string; label: string }[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = value.includes(o.id);
        return (
          <button key={o.id} type="button" aria-pressed={on} className={cx("rounded-full px-2.5 py-1 text-xs font-medium", on ? "bg-brand text-white" : "bg-mist text-ink-soft hover:text-ink")} onClick={() => onChange(on ? value.filter((x) => x !== o.id) : [...value, o.id])}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function ViewPicker({ views, viewId, canSave, busy, onPick, onSave, onDelete }: { views: MapView[]; viewId: string; canSave: boolean; busy: boolean; onPick: (v: MapView | undefined) => void; onSave: (name: string) => void; onDelete: () => void }) {
  const [naming, setNaming] = useState<string | null>(null);
  const current = views.find((v) => v.id === viewId);
  return (
    <div>
      <SelectField label="Saved view" value={viewId} onChange={(e) => onPick(views.find((v) => v.id === e.target.value))}>
        <option value="">My filters</option>
        {views.map((v) => (
          <option key={v.id} value={v.id}>
            {v.name}
          </option>
        ))}
      </SelectField>
      {canSave &&
        (naming === null ? (
          <div className="mt-1.5 flex gap-3 text-xs">
            <button className="font-semibold text-brand hover:underline" onClick={() => setNaming(current?.name ?? "")}>
              {current ? "Update or save as…" : "Save as a view…"}
            </button>
            {current && (
              <button className="text-muted hover:text-danger hover:underline" disabled={busy} onClick={onDelete}>
                Delete "{current.name}"
              </button>
            )}
          </div>
        ) : (
          <form
            className="mt-1.5 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (naming.trim()) onSave(naming.trim());
              setNaming(null);
            }}
          >
            <input autoFocus className={inputClass} placeholder="View name (everyone sees it)" value={naming} maxLength={60} onChange={(e) => setNaming(e.target.value)} />
            <Button type="submit" variant="primary" busy={busy}>
              Save
            </Button>
          </form>
        ))}
    </div>
  );
}

function LegendBox({ legend }: { legend: Legend }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="absolute bottom-3 left-3 z-10 max-w-[14rem] rounded-lg bg-white/95 p-2 text-xs shadow-md">
      <button className="w-full text-left font-semibold text-ink-soft" onClick={() => setOpen(!open)}>
        {open ? "Legend ▾" : "Legend ▸"}
      </button>
      {open && (
        <ul className="mt-1 max-h-48 overflow-y-auto">
          {legend.map((l) => (
            <li key={l.label} className="flex items-center gap-1.5 py-0.5">
              <span className="size-2.5 shrink-0 rounded-full" style={{ background: l.color }} />
              <span className="truncate">{l.label}</span>
              <span className="ml-auto pl-2 text-muted">{l.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function JobCard({ job, canPlace, onClose, onMove, onBackToAddress }: { job: MapJob; canPlace: boolean; onClose: () => void; onMove: () => void; onBackToAddress: () => void }) {
  const directions = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.address || `${job.lat},${job.lon}`)}`;
  return (
    <div className="absolute right-3 bottom-3 left-3 z-20 max-w-md rounded-xl bg-white p-4 shadow-lg sm:left-auto sm:w-96">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <Link to={`/jobs/${job.id}`} className="block truncate text-base font-semibold hover:underline">
            #{job.number} {job.customerName}
          </Link>
          <div className="truncate text-sm text-muted">{job.address}</div>
        </div>
        <button className="text-muted hover:text-ink" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
        <StageBadge name={job.stageName} kind={job.stageKind} />
        <span className="text-muted">{job.workflowName}</span>
        {job.salesRep && <span className="text-muted">· {job.salesRep.name}</span>}
        {job.valueCents !== null && <span className="font-semibold">· {formatCents(job.valueCents)}</span>}
        {job.carrier && <span className="text-muted">· {job.carrier}</span>}
      </div>
      {job.title && <div className="mt-1 truncate text-sm">{job.title}</div>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Link to={`/jobs/${job.id}`} className="rounded-full bg-brand px-3.5 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep">
          Open job
        </Link>
        <a href={directions} target="_blank" rel="noreferrer" className="rounded-full border border-line bg-white px-3.5 py-1.5 text-sm font-semibold text-ink-soft hover:border-brand hover:text-brand">
          Directions
        </a>
        {job.address && <AddressActions address={job.address} jobId={job.id} />}
      </div>
      {canPlace && (
        <div className="mt-2 flex gap-3 text-xs">
          <button className="text-muted hover:text-ink hover:underline" onClick={onMove}>
            Pin in the wrong place? Move it
          </button>
          {job.placed === "manual" && (
            <button className="text-muted hover:text-ink hover:underline" onClick={onBackToAddress}>
              Back to its address
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function HailCard({ report, miles, jobsNear, onClose }: { report: HailReport; miles: number; jobsNear: number; onClose: () => void }) {
  const day = new Date(`${report.day}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  return (
    <div className="absolute right-3 bottom-3 left-3 z-20 max-w-md rounded-xl bg-white p-4 shadow-lg sm:left-auto sm:w-96">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-base font-semibold">
            <span className="size-3 rounded-full" style={{ background: hailColor(report.sizeInches) }} />
            {report.sizeInches}" hail ({hailName(report.sizeInches).toLowerCase()})
          </div>
          <div className="text-sm text-muted">
            {day} · {report.place}, {report.county} County, {report.state}
          </div>
        </div>
        <button className="text-muted hover:text-ink" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
      {report.comments && <p className="mt-2 text-sm">{report.comments}</p>}
      <p className="mt-2 text-sm font-semibold">
        {jobsNear} job{jobsNear === 1 ? "" : "s"} shown within {miles} mi
      </p>
      <p className="mt-1 text-xs text-muted">NOAA Storm Prediction Center report (preliminary). The day runs 7am to 7am Central.</p>
    </div>
  );
}

function JobList({ jobs, hasMoney }: { jobs: MapJob[]; hasMoney: boolean }) {
  const [limit, setLimit] = useState(200);
  return (
    <div className="overflow-hidden rounded-[var(--radius-panel)] bg-white shadow-[var(--shadow-card)]">
      <ul>
        {jobs.slice(0, limit).map((j) => (
          <li key={j.id} className="border-b border-mist last:border-0">
            <Link to={`/jobs/${j.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-mist/50">
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold">
                  #{j.number} {j.customerName}
                </span>
                <span className="block truncate text-xs text-muted">{[j.address || "No address", j.workflowName, j.salesRep?.name].filter(Boolean).join(" · ")}</span>
              </span>
              {hasMoney && j.valueCents !== null && <span className="hidden text-sm sm:block">{formatCents(j.valueCents)}</span>}
              <StageBadge name={j.stageName} kind={j.stageKind} />
            </Link>
          </li>
        ))}
      </ul>
      {jobs.length > limit && (
        <button className="w-full py-3 text-sm font-semibold text-brand hover:underline" onClick={() => setLimit(limit + 500)}>
          Show more ({jobs.length - limit} left)
        </button>
      )}
      {!jobs.length && <div className="px-5 py-10 text-center text-ink-soft">No jobs match these filters.</div>}
    </div>
  );
}

function DayList({ stops, loading }: { stops: MapAppointment[]; loading: boolean }) {
  const placed = stops.filter((s) => s.lat !== null);
  const route = placed.length ? `https://www.google.com/maps/dir/${placed.map((s) => encodeURIComponent(s.location)).join("/")}` : null;
  const time = (s: MapAppointment) => (s.allDay ? "All day" : new Date(s.startsAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }));
  return (
    <div className="mt-4 rounded-[var(--radius-panel)] bg-white p-4 shadow-[var(--shadow-card)]">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-semibold">The day's stops</h3>
        {route && (
          <a href={route} target="_blank" rel="noreferrer" className="text-sm font-semibold text-brand hover:underline">
            Open the route in Google Maps
          </a>
        )}
      </div>
      {loading ? (
        <Loading />
      ) : !stops.length ? (
        <p className="text-sm text-muted">No appointments that day.</p>
      ) : (
        <ol className="text-sm">
          {stops.map((s) => {
            const n = placed.indexOf(s) + 1;
            return (
              <li key={s.id} className="flex gap-3 border-b border-mist py-1.5 last:border-0">
                <span className={cx("flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-bold", n ? "bg-ink text-white" : "bg-mist text-muted")}>{n || "–"}</span>
                <span className="w-16 shrink-0 text-muted">{time(s)}</span>
                <span className="min-w-0">
                  <span className="block truncate font-medium">{s.jobId ? <Link to={`/jobs/${s.jobId}`} className="hover:underline">{s.title}</Link> : s.title}</span>
                  <span className="block truncate text-xs text-muted">{s.location ? (n ? s.location : `${s.location} (couldn't place it)`) : "No address"}</span>
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

type MapProps = {
  apiKey: string;
  mapId: string;
  jobs: MapJob[];
  colorOf: (j: MapJob) => { color: string; label: string };
  heat: boolean;
  hail: HailReport[];
  hailMiles: number;
  stops: MapAppointment[];
  spot: { lat: number; lon: number } | null;
  spotMiles: number;
  home: { lat: number; lon: number } | null;
  crosshair: boolean;
  fitTick: number;
  selectedId: string | null;
  onJob: (id: string) => void;
  onHail: (h: HailReport) => void;
  onClick: (at: { lat: number; lon: number }) => void;
  onError: (message: string) => void;
};

/** The Google map with the jobs (clustered), heat, hail, a day's route, and a spot's circle. */
function JobsMap(props: MapProps) {
  const el = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const live = useRef(props);
  live.current = props;
  const markers = useRef(new Map<string, { marker: google.maps.marker.AdvancedMarkerElement; color: string }>());
  const clusterer = useRef<MarkerClusterer | null>(null);
  const heat = useRef<HeatOverlay | null>(null);
  const fitted = useRef(false);

  // Load Google Maps and make the map once.
  useEffect(() => {
    let gone = false;
    onGoogleMapsAuthFailure(() => live.current.onError("Google Maps didn't accept the key on this address. Check the key's website restrictions in Google Cloud."));
    loadGoogleMaps(props.apiKey)
      .then(async () => {
        await Promise.all([google.maps.importLibrary("maps"), google.maps.importLibrary("marker")]);
        if (gone || !el.current) return;
        const center = props.home ?? { lat: 35.4676, lon: -97.5164 };
        const m = new google.maps.Map(el.current, {
          center: { lat: center.lat, lng: center.lon },
          zoom: 10,
          mapId: props.mapId,
          gestureHandling: "greedy",
          clickableIcons: false,
          streetViewControl: false,
          mapTypeControl: true,
          mapTypeControlOptions: { mapTypeIds: ["roadmap", "hybrid"], style: google.maps.MapTypeControlStyle.DROPDOWN_MENU },
          fullscreenControl: true,
        });
        m.addListener("click", (e: google.maps.MapMouseEvent) => e.latLng && live.current.onClick({ lat: e.latLng.lat(), lon: e.latLng.lng() }));
        clusterer.current = new MarkerClusterer({ map: m, markers: [] });
        setMap(m);
      })
      .catch((err: unknown) => live.current.onError(err instanceof Error ? err.message : "Couldn't load Google Maps."));
    return () => {
      gone = true;
    };
    // The key doesn't change while the page is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pins: made once per job, recolored when the coloring changes, and clustered when zoomed out.
  useEffect(() => {
    if (!map || !clusterer.current) return;
    const keep = new Set<string>();
    const list: google.maps.marker.AdvancedMarkerElement[] = [];
    for (const j of props.jobs) {
      keep.add(j.id);
      const { color } = props.colorOf(j);
      let m = markers.current.get(j.id);
      const pinFor = () => new google.maps.marker.PinElement({ background: color, borderColor: "#ffffff", glyphColor: "#ffffff", scale: 0.85 });
      if (!m) {
        const marker = new google.maps.marker.AdvancedMarkerElement({ position: { lat: j.lat!, lng: j.lon! }, content: pinFor(), title: `#${j.number} ${j.customerName}`, gmpClickable: true });
        marker.addEventListener("gmp-click", () => live.current.onJob(j.id));
        m = { marker, color };
        markers.current.set(j.id, m);
      } else {
        if (m.color !== color) {
          m.marker.content = pinFor();
          m.color = color;
        }
        m.marker.position = { lat: j.lat!, lng: j.lon! };
      }
      list.push(m.marker);
    }
    for (const [id, m] of markers.current) if (!keep.has(id)) (m.marker.map = null), markers.current.delete(id);
    clusterer.current.clearMarkers(true);
    clusterer.current.addMarkers(list);
  }, [map, props.jobs, props.colorOf]);

  // Fit to the jobs the first time there are some, and when asked.
  useEffect(() => {
    if (!map) return;
    if (fitted.current && !props.fitTick) return;
    const pts = props.jobs;
    if (!pts.length) return;
    const b = new google.maps.LatLngBounds();
    for (const j of pts) b.extend({ lat: j.lat!, lng: j.lon! });
    map.fitBounds(b, 40);
    fitted.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, props.fitTick, props.jobs.length > 0]);

  useEffect(() => {
    if (!map) return;
    if (props.heat) {
      heat.current ??= createHeatOverlay();
      heat.current.setMap(map);
      heat.current.setPoints(props.jobs.map((j) => ({ lat: j.lat!, lon: j.lon! })));
    } else heat.current?.setMap(null);
  }, [map, props.heat, props.jobs]);

  // Hail: a circle per report, as wide as "near" and colored by size.
  useEffect(() => {
    if (!map) return;
    const circles = props.hail.map((h) => {
      const c = new google.maps.Circle({ map, center: { lat: h.lat, lng: h.lon }, radius: props.hailMiles * METERS_PER_MILE, strokeColor: hailColor(h.sizeInches), strokeWeight: 1, strokeOpacity: 0.8, fillColor: hailColor(h.sizeInches), fillOpacity: 0.18, zIndex: 1 });
      c.addListener("click", () => live.current.onHail(h));
      return c;
    });
    return () => circles.forEach((c) => c.setMap(null));
  }, [map, props.hail, props.hailMiles]);

  // A day's stops: numbered, joined in order.
  useEffect(() => {
    if (!map) return;
    const placed = props.stops.filter((s) => s.lat !== null && s.lon !== null);
    const made = placed.map((s, i) => {
      const pin = new google.maps.marker.PinElement({ background: "#111827", borderColor: "#ffffff", glyphText: String(i + 1), glyphColor: "#ffffff", scale: 1.1 });
      return new google.maps.marker.AdvancedMarkerElement({ map, position: { lat: s.lat!, lng: s.lon! }, content: pin, title: `${i + 1}. ${s.title}`, zIndex: 1000 });
    });
    const line = placed.length > 1 ? new google.maps.Polyline({ map, path: placed.map((s) => ({ lat: s.lat!, lng: s.lon! })), strokeColor: "#111827", strokeOpacity: 0.7, strokeWeight: 3 }) : null;
    if (placed.length) {
      const b = new google.maps.LatLngBounds();
      placed.forEach((s) => b.extend({ lat: s.lat!, lng: s.lon! }));
      map.fitBounds(b, 80);
    }
    return () => {
      made.forEach((m) => (m.map = null));
      line?.setMap(null);
    };
  }, [map, props.stops]);

  useEffect(() => {
    if (!map || !props.spot) return;
    const c = new google.maps.Circle({ map, center: { lat: props.spot.lat, lng: props.spot.lon }, radius: props.spotMiles * METERS_PER_MILE, strokeColor: "#111827", strokeWeight: 2, fillColor: "#111827", fillOpacity: 0.06, clickable: false });
    map.fitBounds(c.getBounds()!, 20);
    return () => c.setMap(null);
  }, [map, props.spot, props.spotMiles]);

  // The picked job's pin stands out; pan to it if it's off screen.
  useEffect(() => {
    if (!map || !props.selectedId) return;
    const m = markers.current.get(props.selectedId);
    if (!m) return;
    m.marker.zIndex = 999;
    const at = m.marker.position as google.maps.LatLngLiteral;
    if (at && !map.getBounds()?.contains(at)) map.panTo(at);
    if ((map.getZoom() ?? 0) < 14) map.setZoom(15);
    return () => {
      m.marker.zIndex = null;
    };
  }, [map, props.selectedId]);

  useEffect(() => {
    map?.setOptions({ draggableCursor: props.crosshair ? "crosshair" : null });
  }, [map, props.crosshair]);

  return <div ref={el} className="size-full bg-mist" />;
}
